// Bounded, observable and repeatable /approve for the two result pages (Swingies and MiniMove).
//
// * sendApprove: the POST is bounded by APPROVE_TIMEOUT_MS. An abort and a thrown fetch both reject with an
//   error that names the failure ("aborted" | "network_error"), so the page's existing catch path (status
//   poll by the persisted request id) takes over exactly as it did for a thrown fetch before. An ERROR answer
//   (4xx / 5xx, e.g. a proxy page) whose body is not JSON keeps its HTTP status on the error (httpStatus), so the
//   telemetry reports the number the server sent; a 2xx whose body breaks off stays a network error.
// * reportApproveTiming: the client half of approve_timings.csv (route /api/attraction/approve-timing-report).
//   Fire-and-forget with keepalive, so an aborted / thrown approve is on record too, not only an answered one.
//   Since 2026-10-31 it also says which attempt the row is (APPROVE_ATTEMPTS) and whether the lease was renewed or
//   the faces acquired again first (APPROVE_LEASE); the server adds the browser from the report's own User-Agent.
// * useApproveRetry: after a thrown / aborted approve whose status poll answers 410 (claim_unknown: the server
//   never recorded the request) the page re-sends ONCE, silently. If that is lost too the guest sees a "נסה שוב"
//   button that re-sends again, for as long as the flow lives. Before every re-send the page asks
//   approveRecovery.prepareResend() for a usable hold: the same request (same request id and claim token) after a
//   lease renewal, or a NEW request after the same faces were acquired again. The request id is the idempotency
//   key: if an earlier attempt did complete on the server, a repeat with the same id returns that claim. A re-send
//   that finds the earlier attempt still in server intake (409, isApproveBusy) waits APPROVE_BUSY_WAIT_MS and asks
//   the claim instead of being read as "faces in use". Each re-send persists the claim handle again (the lost
//   screen cleared it) before it sends.
//
// EDITING LEASE: the server's editing lease on the chosen faces is 105 s (minime-web
// Constants.FACE_CLAIM_EDIT_LEASE_S, mirrored by APPROVE_LEASE_MS) and the activity renewal stops once the approve is
// tapped (claimRenewal.js refuses a token whose approveRid is stored); after it the server answers claim_expired
// (410). So the first POST is bounded just under it (APPROVE_TIMEOUT_MS = 100 s), counted from the guest's approve
// tap (ensureApproveRequest(): persisted with the id as approveAt, so a tap that reuses a stored id after a reload
// keeps the FIRST tap's clock). Until 2026-10-31 a re-send was attempted only while at least MIN_RESEND_LEASE_MS of
// that lease was left, so a late loss (KARNI, 2026-10-25: three guests lost at ~91 s) got neither a re-send nor a
// button. Since then the page renews the lease (or acquires the faces again) before a re-send, and the hook keeps
// the lease END it got back (adopt()), not the tap time. A re-send is bounded by min(APPROVE_TIMEOUT_MS,
// leaseLeft - 5 s), so it ends before the lease. This module does not touch holds or claim state.
import { useCallback, useRef, useState } from "react";

// Bound of one /approve POST: just under the server's editing lease (APPROVE_LEASE_MS).
export const APPROVE_TIMEOUT_MS = 100000;

// Mirrors minime-web app/constants.py Constants.FACE_CLAIM_EDIT_LEASE_S (105 s): the editing lease on the chosen
// faces. The activity renewal stops at the approve tap, so the client counts it from that tap until a re-send renews
// it. Keep in step with the server.
export const APPROVE_LEASE_MS = 105000;

// A re-send needs at least this much lease left; below it the page renews the lease or acquires the faces again.
export const MIN_RESEND_LEASE_MS = 20000;

// A re-send ends this long before the lease does: the server still reads, hashes and decodes the body, takes the
// guest lock and checks the heads in Dropbox (1-3 s measured) before its claim script compares the lease.
export const RESEND_LEASE_MARGIN_MS = 5000;

// `status` values of the client timing row when no HTTP answer was read (numbers are HTTP statuses).
export const APPROVE_ABORTED = "aborted";
export const APPROVE_NETWORK_ERROR = "network_error";

// Silent automatic re-sends per guest tap on "אשר ושלח" (the button re-sends without limit, one tap each).
export const SILENT_RESENDS = 1;

// Which attempt a timing row is (the "ניסיון" cell of approve_timings.csv): the guest's tap, the silent re-send, or
// a tap on "נסה שוב"; `lease` says whether the hold was renewed or the faces acquired again before it.
export const APPROVE_ATTEMPTS = Object.freeze({ FIRST: "first", AUTO: "auto", BUTTON: "button" });
export const APPROVE_LEASE = Object.freeze({ RENEWED: "renewed", REACQUIRED: "reacquired" });

// A re-send that the server answers 409 with no code and no face list ("Another operation is in progress": the
// per-guest intake lock) met an earlier attempt of the SAME approve that is still being taken in. The page then
// waits this long and asks the claim by request id, instead of reading the 409 as "faces in use". A chosen
// settle time for a request whose intake is a body decode plus a Dropbox check per face, not a measured value.
export const APPROVE_BUSY_WAIT_MS = 4000;

// Hebrew UI texts of the retry offer.
export const MSG_APPROVE_RETRY = "התמונה לא נשלחה, אפשר לנסות שוב";
export const MSG_APPROVE_RETRY_OFFLINE = "אין חיבור כרגע, אפשר לנסות שוב בעוד רגע";
export const LABEL_APPROVE_RETRY = "נסה שוב";

const failure = (kind, cause, httpStatus) => {
  const error = new Error(`approve ${kind}`);
  error.approveFailure = kind;
  error.cause = cause;
  if (httpStatus !== undefined) error.httpStatus = httpStatus;
  return error;
};

// The telemetry `status` of a request that threw: the HTTP status of an error answer whose body was unreadable,
// "aborted" when the bound fired, else "network_error".
export const approveFailureStatus = (error) =>
  (error && (error.httpStatus || error.approveFailure)) || APPROVE_NETWORK_ERROR;

// True for the answer of a RE-SENT approve that found the earlier attempt still in server intake: 409 carrying
// none of the codes and face lists the other 409 sites send (see the page's answer handling).
export const isApproveBusy = (response, data) =>
  response.status === 409 && !(data && (data.code || data.used_faces || data.held_faces));

// POST the approve body as JSON. Resolves { response, data } (data = the parsed JSON body). The WHOLE exchange
// (headers and body) is bounded: the deadline aborts the request and rejects even when the fetch implementation
// ignores the signal. A body that is not JSON rejects too (as a network error) unless `tolerateBadBody`, which
// reads it as {} (MiniMove's historic behavior).
export const sendApprove = async ({ url, body, timeoutMs = APPROVE_TIMEOUT_MS, tolerateBadBody = false }) => {
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  let timer = null;
  let timedOut = false;
  let answeredStatus; // the HTTP status once headers arrived (kept for an error answer whose body is not JSON)
  const exchange = (async () => {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      ...(controller ? { signal: controller.signal } : {}),
    });
    answeredStatus = response.status;
    const data = tolerateBadBody ? await response.json().catch(() => ({})) : await response.json();
    return { response, data };
  })();
  const deadline = new Promise((_, reject) => {
    timer = setTimeout(() => {
      timedOut = true;
      reject(failure(APPROVE_ABORTED));
      if (controller) controller.abort();
    }, timeoutMs);
  });
  exchange.catch(() => {}); // a rejection that lost the race is not unhandled
  try {
    return await Promise.race([exchange, deadline]);
  } catch (error) {
    // A deadline that fired is "aborted" whatever had arrived. Otherwise an error answer (>= 400) with an
    // unreadable body still carries its status; a success status with a broken body is a network error.
    const httpStatus = !timedOut && answeredStatus >= 400 ? answeredStatus : undefined;
    throw failure(timedOut ? APPROVE_ABORTED : APPROVE_NETWORK_ERROR, error, httpStatus);
  } finally {
    clearTimeout(timer);
  }
};

// Posts the client row of ONE approve attempt. `status` is the HTTP status of the answer, or
// APPROVE_ABORTED / APPROVE_NETWORK_ERROR. Optional fields (trimS, pixelRatio, attempt, lease) are left out of the
// body when undefined (MiniMove reports neither trimS nor pixelRatio). Never throws and never blocks the guest flow.
export const reportApproveTiming = ({
  serverUrl,
  eventName,
  attraction,
  userId,
  requestStartMs,
  payloadKb,
  trimS,
  pixelRatio,
  status,
  attempt,
  lease,
}) => {
  try {
    fetch(`${serverUrl}/api/attraction/approve-timing-report`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true, // survives the imminent navigation
      body: JSON.stringify({
        event_name: eventName,
        attraction,
        user_id: userId,
        ...(trimS !== undefined ? { trim_s: trimS } : {}),
        ...(pixelRatio !== undefined ? { pixel_ratio: pixelRatio } : {}),
        payload_kb: payloadKb,
        request_s: Math.round((performance.now() - requestStartMs) / 100) / 10,
        status,
        ...(attempt ? { attempt } : {}),
        ...(lease ? { lease } : {}),
      }),
    }).catch(() => {});
  } catch (error) {
    // a report must never break the approve flow
  }
};

// The end (epoch ms) of the editing lease that started with the approve tap at `tapAt`, or null when unknown.
export const leaseEndFromTap = (tapAt) => (typeof tapAt === "number" ? tapAt + APPROVE_LEASE_MS : null);

// The bound of a re-send under the lease ending at `leaseEndAt` (epoch ms), or null when the lease left is too short
// to re-send in: min(APPROVE_TIMEOUT_MS, leaseLeft - 5 s) while leaseLeft >= MIN_RESEND_LEASE_MS.
export const resendTimeoutMs = (leaseEndAt, now = Date.now()) => {
  if (typeof leaseEndAt !== "number") return null;
  const leaseLeft = leaseEndAt - now;
  if (!(leaseLeft >= MIN_RESEND_LEASE_MS)) return null;
  return Math.min(APPROVE_TIMEOUT_MS, leaseLeft - RESEND_LEASE_MARGIN_MS);
};

// Retry state of one result page. `request` is whatever the page needs to repeat the approve (it stores
// { approveRid, claimToken, claimState, download }), kept here so a re-send never re-derives the id from storage
// that the lost screen has already cleared.
export const useApproveRetry = () => {
  const [offered, setOffered] = useState(false);
  const [offerMessage, setOfferMessage] = useState(MSG_APPROVE_RETRY);
  const requestRef = useRef(null);
  const silentLeftRef = useRef(0);
  const offeredRef = useRef(false);
  const leaseEndRef = useRef(null);

  // A guest tap on "אשר ושלח": a new approve, one silent re-send available, no offer showing. `tapAt` (epoch ms) is
  // the moment the approve's request id was minted; the page passes the persisted approveAt, so a tap that reuses an
  // id from an earlier tap does not restart the clock. Missing / not a number = the lease end is unknown (a re-send
  // then renews or acquires first, see approveRecovery.js).
  const begin = useCallback((request, tapAt) => {
    requestRef.current = request;
    leaseEndRef.current = leaseEndFromTap(tapAt);
    silentLeftRef.current = SILENT_RESENDS;
    offeredRef.current = false;
    setOffered(false);
  }, []);

  // The request a re-send now uses and the lease end it holds (a renewal keeps the request, a new acquisition
  // replaces it). The silent re-send budget is not touched.
  const adopt = useCallback((request, leaseEndAt) => {
    requestRef.current = request;
    leaseEndRef.current = typeof leaseEndAt === "number" ? leaseEndAt : null;
  }, []);

  // The request the next re-send repeats, and the lease end it holds (null = unknown).
  const current = useCallback(() => requestRef.current, []);
  const leaseEnd = useCallback(() => leaseEndRef.current, []);

  // The bound (ms) of a re-send made now under the held lease, or null when too little of it is left. Read-only.
  const resendBudget = useCallback(() => resendTimeoutMs(leaseEndRef.current), []);

  // The approve was lost in transit (thrown / aborted, then 410 claim_unknown): true = re-send now, silently.
  const takeSilentResend = useCallback(() => {
    if (silentLeftRef.current <= 0) return false;
    silentLeftRef.current -= 1;
    return true;
  }, []);

  // Show the "נסה שוב" button with `message` (default MSG_APPROVE_RETRY).
  const offer = useCallback((message = MSG_APPROVE_RETRY) => {
    offeredRef.current = true;
    setOfferMessage(message);
    setOffered(true);
  }, []);

  // The button was tapped: the request to re-send, or null when nothing is offered / a double tap found the
  // offer already taken (the ref flips synchronously, before any re-render could hide the button).
  const takeOffer = useCallback(() => {
    if (!offeredRef.current) return null;
    offeredRef.current = false;
    setOffered(false);
    return requestRef.current;
  }, []);

  return {
    offered,
    offerMessage,
    begin,
    adopt,
    current,
    leaseEnd,
    resendBudget,
    takeSilentResend,
    offer,
    takeOffer,
  };
};
